import { IonPopover, IonSkeletonText } from "@ionic/react";
import { isSameDay, isSameMonth, isToday } from "date-fns";
import { useCallback, useMemo } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import DateStamp from "@components/dates/DateStamp";
import styles from "@components/expertSection/schedule/ScheduleSession.module.css";
import ButtonThatLooksLikeASelect from "@components/fieldsNative/ButtonThatLooksLikeASelect";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import useNow from "@hooks/useNow";
import useOpener from "@hooks/useOpener";
import { ConsultAvailability, ConsultDurationEnum, GetConsultAvailabilityDaysQuery } from "@typing/Generated";
import { getDaysOfTheWeekStartingWithSunday, getWeeksForCurrentMonth } from "@utils/timeUtils";

type Props = {
  consultAvailabilityDays?: GetConsultAvailabilityDaysQuery["consultAvailabilityDays"];
  currentStartOfMonth: Date;
  duration: ConsultDurationEnum | null;
  selectedDay: Date | null;
  setAvailabilitySelected: (availability: ConsultAvailability | null) => void;
  setCurrentStartOfMonth: (date: Date) => void;
  setSelectedDay: (date: Date | null) => void;
};

const ExpertAvailabilityCalendarForExperts = ({
  consultAvailabilityDays,
  currentStartOfMonth,
  duration,
  selectedDay,
  setAvailabilitySelected,
  setCurrentStartOfMonth,
  setSelectedDay
}: Props) => {
  const calendarOpener = useOpener();

  const now = useNow();
  const calendarWeeks = useMemo(() => getWeeksForCurrentMonth({ date: currentStartOfMonth }), [currentStartOfMonth]);

  const advanceMonth = useCallback(
    (count: number) => {
      const startOfMonth = new Date(
        currentStartOfMonth.getFullYear(),
        currentStartOfMonth.getMonth() + count,
        1,
        currentStartOfMonth.getHours(),
        currentStartOfMonth.getMinutes(),
        currentStartOfMonth.getSeconds()
      );
      setCurrentStartOfMonth(startOfMonth);
    },
    [currentStartOfMonth, setCurrentStartOfMonth]
  );

  const handlePreviousMonth = useCallback(() => {
    advanceMonth(-1);
    setSelectedDay(null);
    setAvailabilitySelected(null);
  }, [advanceMonth, setAvailabilitySelected, setSelectedDay]);

  const handleNextMonth = useCallback(() => {
    advanceMonth(1);
    setSelectedDay(null);
    setAvailabilitySelected(null);
  }, [advanceMonth, setAvailabilitySelected, setSelectedDay]);

  const nextMonthAllowed = useMemo(() => {
    const outOfScopeMonth = new Date(now.getFullYear(), now.getMonth() + 3, 2).getMonth();
    const nextMonth = new Date(currentStartOfMonth.getFullYear(), currentStartOfMonth.getMonth() + 1, 1, 0, 0, 0);
    return nextMonth.getMonth() !== outOfScopeMonth;
  }, [currentStartOfMonth, now]);

  const previousMonthAllowed = useMemo(() => {
    const badMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
    const previousMonth = new Date(currentStartOfMonth.getFullYear(), currentStartOfMonth.getMonth() - 1, 1, 0, 0, 0);
    return previousMonth.getMonth() !== badMonth.getMonth();
  }, [currentStartOfMonth, now]);

  const handleSelectedDayChange = (date: Date) => {
    calendarOpener.close();
    setSelectedDay(date);
    setAvailabilitySelected(null);
  };

  return (
    <>
      <ButtonThatLooksLikeASelect disabled={!duration} onClick={calendarOpener.open}>
        {selectedDay ? <DateStamp value={selectedDay} /> : <FormattedMessage id="forms.consult.mmddyy" />}
      </ButtonThatLooksLikeASelect>
      <IonPopover
        className={styles.calendarPopover}
        isOpen={calendarOpener.isOpen}
        onIonPopoverDidDismiss={calendarOpener.close}
        onIonPopoverWillPresent={calendarOpener.open}
      >
        <div className={`${styles.calendar} ${styles.expertCalendar}`}>
          <div className={styles.rescheduleMonthSelector}>
            <ActionIconButton
              ariaLabelKey="accessibility.ariaLabels.previous"
              disabled={!previousMonthAllowed}
              icon="chevronLeft"
              onClick={handlePreviousMonth}
              skipSpacing
            />
            <Heading2 color="gray-500">
              <FormattedDate month="long" timeZone="utc" value={currentStartOfMonth} />
            </Heading2>
            <ActionIconButton
              ariaLabelKey="accessibility.ariaLabels.next"
              disabled={!nextMonthAllowed}
              icon="chevronRight"
              onClick={handleNextMonth}
              skipSpacing
            />
          </div>
          <div className={styles.calendarDays}>
            {getDaysOfTheWeekStartingWithSunday().map(day => (
              <div className={styles.calendarDay} key={day}>
                <BodyCopy color="gray-500">{day.slice(0, 1).toUpperCase()}</BodyCopy>
              </div>
            ))}
          </div>
          {Object.keys(calendarWeeks).map(weekIndex => (
            <div className={styles.calendarDays} key={weekIndex}>
              {calendarWeeks[weekIndex].map(({ date, index }) => {
                const availabilityDays = consultAvailabilityDays?.filter(availabilityDay =>
                  isSameDay(availabilityDay.date, date)
                );
                const isEmpty =
                  !availabilityDays ||
                  availabilityDays.length === 0 ||
                  availabilityDays.flatMap(ads => ads.availabilities).length === 0;
                const selected = selectedDay && isSameDay(selectedDay, date);
                return (
                  <div
                    className={`${styles.calendarDay} ${isToday(date) ? styles.currentDateNumber : ""} ${
                      isEmpty ? styles.disabledNumber : selected ? styles.selectedDay : styles.availableDay
                    }`}
                    key={index}
                    onClick={() => {
                      if (!isEmpty) {
                        handleSelectedDayChange(date);
                      }
                    }}
                  >
                    {isSameMonth(date, currentStartOfMonth) &&
                      (consultAvailabilityDays ? (
                        <div className={styles.dateNumber}>
                          <FormattedDate day="numeric" timeZone="utc" value={date} />
                        </div>
                      ) : (
                        <IonSkeletonText animated className={styles.emptyNumber} />
                      ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </IonPopover>
    </>
  );
};

export default ExpertAvailabilityCalendarForExperts;
